import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  validatePartnerCode,
  signupMmt,
  verifySignupOtp,
  registerMmt,
  loginMmt,
} from "../mmtUserList/mmtApi";
import { setStoredSession, buildSessionPath } from "../mmtUserList/mmtFlow";
import { ADMIN_CHOICE, USER_LIST } from "../../routes/routerPath";
import { useSection } from "../../i18n/useSection";
import AuthLayout from "./AuthLayout";
import PasswordRule from "./PasswordRule";
import AuthTerms from "./AuthTerms";
import "./mmtlogin.scss";

const STEPS = {
  PARTNER_CODE: "partnerCode",
  EMAIL: "email",
  OTP: "otp",
  REGISTER: "register",
  SUCCESS: "success",
};
const EMAIL_REGEX = /^\s*[a-z0-9._+-]+@[a-z0-9.-]+\.[a-z]{2,}\s*$/i;

export default function MmtSignupFlow() {
  const navigate = useNavigate();
  const t = useSection("mmtSignup");
  const ui = useSection("mmtAuth");
  const passwordLabels = useSection("mmtLogin");
  const [step, setStep] = useState(STEPS.PARTNER_CODE);
  const [partnerCode, setPartnerCode] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");

  const run = async (fn) => {
    setLoading(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t.error_generic);
    } finally {
      setLoading(false);
    }
  };

  const submitPartnerCode = (event) => {
    event.preventDefault();
    const trimmedCode = partnerCode.trim();
    if (!trimmedCode) {
      setError(t.error_partner_code_required);
      return;
    }
    run(async () => {
      const result = await validatePartnerCode(trimmedCode);
      setPartnerCode(result.data.partner_code);
      setStep(STEPS.EMAIL);
    });
  };

  const submitEmail = (event) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError(t.error_invalid_email);
      return;
    }
    run(async () => {
      await signupMmt(trimmed);
      setStep(STEPS.OTP);
    });
  };

  const submitOtp = (event) => {
    event.preventDefault();
    if (!/^\d{4,8}$/.test(otp.trim())) {
      setError(t.error_invalid_code);
      return;
    }
    run(async () => {
      await verifySignupOtp(email.trim(), otp.trim());
      setStep(STEPS.REGISTER);
    });
  };

  const submitRegister = (event) => {
    event.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError(t.error_missing_name);
      return;
    }
    if (password.length < 8) {
      setError(t.error_password_length);
      return;
    }
    if (password !== confirm) {
      setError(t.error_password_mismatch);
      return;
    }
    run(async () => {
      try {
        await registerMmt({
          email: email.trim(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          partnerCode,
        });
      } catch (err) {
        if (err?.response?.status === 404) {
          setPartnerCode("");
          setStep(STEPS.PARTNER_CODE);
          setError(
            err?.response?.data?.message || err?.message || t.error_generic,
          );
          return;
        }
        throw err;
      }
      const { session } = await loginMmt(email.trim(), password);
      setStoredSession(session);
      setStep(STEPS.SUCCESS);
      const target =
        session.role === "ADMIN"
          ? ADMIN_CHOICE
          : buildSessionPath(USER_LIST, session);
      window.setTimeout(() => {
        navigate(target, { replace: true });
      }, 1200);
    });
  };

  return (
    <AuthLayout screen={`${step}:${t.title}`}>
      {step === STEPS.PARTNER_CODE ? (
        <form
          className="mmt-login-card"
          noValidate
          aria-busy={loading}
          onSubmit={submitPartnerCode}
        >
          <h2 tabIndex={-1}>{t.partner_code_title}</h2>
          <p>{t.partner_code_subtitle}</p>
          {error ? (
            <div className="mmt-login-error" role="alert">
              {error}
            </div>
          ) : null}
          <label className="mmt-field">
            <span>{t.partner_code_label}</span>
            <input
              value={partnerCode}
              onChange={(e) => {
                setPartnerCode(e.target.value);
                setError("");
              }}
              autoCapitalize="none"
              autoComplete="off"
              maxLength={50}
              disabled={loading}
            />
          </label>
          <button
            className="mmt-primary-button"
            type="submit"
            disabled={loading}
          >
            {loading ? t.verifying_partner_code : t.continue}
          </button>
          <p className="mmt-new-user">
            {t.already_have_account} <Link to="/login">{t.log_in}</Link>
          </p>
        </form>
      ) : null}

      {step === STEPS.EMAIL ? (
        <form
          className="mmt-login-card"
          noValidate
          aria-busy={loading}
          onSubmit={submitEmail}
        >
          <h2 tabIndex={-1}>{t.title}</h2>
          {error ? (
            <div className="mmt-login-error" role="alert">
              {error}
            </div>
          ) : null}
          <label className="mmt-field">
            <span>{t.email_label}</span>
            <input
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              type="email"
              autoCapitalize="off"
              spellCheck={false}
              inputMode="email"
              autoComplete="email"
              placeholder="name@organization.com"
              disabled={loading}
            />
          </label>
          <button
            className="mmt-primary-button"
            type="submit"
            disabled={loading}
          >
            {loading ? t.sending : t.send_code}
          </button>
          <p className="mmt-new-user">
            {t.already_have_account} <Link to="/login">{t.log_in}</Link>
          </p>
        </form>
      ) : null}

      {step === STEPS.OTP ? (
        <form
          className="mmt-login-card"
          noValidate
          aria-busy={loading}
          onSubmit={submitOtp}
        >
          <h2 tabIndex={-1}>{t.verify_email_title}</h2>
          <p className="mmt-auth-hint">
            {t.code_sent.split("{email}")[0]}
            <b>{email.trim()}</b>
            {t.code_sent.split("{email}")[1]}
          </p>
          {notice && (
            <p className="mmt-auth-notice" role="status">
              {notice}
            </p>
          )}
          {error ? (
            <div className="mmt-login-error" role="alert">
              {error}
            </div>
          ) : null}
          <label className="mmt-field">
            <span>{t.code_label}</span>
            <input
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value);
                setError("");
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              className="mmt-code-input"
              disabled={loading}
            />
          </label>
          <button
            className="mmt-primary-button"
            type="submit"
            disabled={loading}
          >
            {loading ? t.verifying : t.verify}
          </button>
          <div className="mmt-auth-actions">
            <button
              type="button"
              className="mmt-auth-link"
              disabled={loading}
              onClick={() => {
                setNotice("");
                run(async () => {
                  await signupMmt(email.trim());
                  setNotice(ui.code_resent);
                });
              }}
            >
              {ui.resend}
            </button>
            <button
              type="button"
              className="mmt-auth-link"
              disabled={loading}
              onClick={() => {
                setError("");
                setNotice("");
                setOtp("");
                setStep(STEPS.EMAIL);
              }}
            >
              {ui.change_email}
            </button>
          </div>
        </form>
      ) : null}

      {step === STEPS.REGISTER ? (
        <form
          className="mmt-login-card"
          noValidate
          aria-busy={loading}
          onSubmit={submitRegister}
        >
          <h2 tabIndex={-1}>{t.create_account_title}</h2>
          {error ? (
            <div className="mmt-login-error" role="alert">
              {error}
            </div>
          ) : null}
          <label className="mmt-field">
            <span>{t.first_name}</span>
            <input
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading}
            />
          </label>
          <label className="mmt-field">
            <span>{t.last_name}</span>
            <input
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading}
            />
          </label>
          <div className="mmt-field">
            <label htmlFor="signup-password">{t.password}</label>
            <div className="mmt-password-wrap">
              <input
                id="signup-password"
                aria-describedby="signup-password-rules"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                className="mmt-icon-button"
                aria-controls="signup-password"
                aria-pressed={showPassword}
                onClick={() => setShowPassword((value) => !value)}
                aria-label={
                  showPassword
                    ? passwordLabels.hide_password
                    : passwordLabels.show_password
                }
              >
                {showPassword ? passwordLabels.hide : passwordLabels.show}
              </button>
            </div>
            <PasswordRule password={password} id="signup-password-rules" />
          </div>
          <div className="mmt-field">
            <label htmlFor="signup-confirm">{t.confirm_password}</label>
            <div className="mmt-password-wrap">
              <input
                id="signup-confirm"
                type={showConfirm ? "text" : "password"}
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setError("");
                }}
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                className="mmt-icon-button"
                aria-controls="signup-confirm"
                aria-pressed={showConfirm}
                onClick={() => setShowConfirm((value) => !value)}
                aria-label={
                  showConfirm
                    ? passwordLabels.hide_password
                    : passwordLabels.show_password
                }
              >
                {showConfirm ? passwordLabels.hide : passwordLabels.show}
              </button>
            </div>
          </div>
          <button
            className="mmt-primary-button"
            type="submit"
            disabled={loading}
          >
            {loading ? t.creating : t.create_account}
          </button>
          <AuthTerms signup />
        </form>
      ) : null}

      {step === STEPS.SUCCESS ? (
        <div className="mmt-login-card">
          <h2 tabIndex={-1}>{t.welcome_title}</h2>
          <p>{t.welcome_subtitle}</p>
        </div>
      ) : null}
    </AuthLayout>
  );
}
